import { useRef, useState } from 'react';
import { readDroppedFiles } from './DroppedEntryReader';

/**
 * File dropzone for the Import tab — drag/drop (files or whole folders) or
 * click-to-browse, multiple files. Pure UI: just hands the resolved file
 * list up (+ whether it came from a folder), no parsing here. Folder
 * recursion lives in DroppedEntryReader.
 * @param {{ onFilesAdded: (files: FileList|File[], isFolder: boolean) => void }} props
 */
export default function ImportFileDropzone({ onFilesAdded }) {
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    // Only used for the click-to-select paths below — webkitRelativePath IS
    // reliably populated by a real <input webkitdirectory> selection (unlike
    // drag-and-drop's FileSystemEntry API, see readDroppedFiles' own isFolder
    // detection for that path).
    const isFolderFile = (file) => Boolean(file.webkitRelativePath);

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        const { files, isFolder } = await readDroppedFiles(e.dataTransfer);
        if (files.length) {
            onFilesAdded(files, isFolder);
        }
    };

    const handleFileInput = (e) => {
        const { files } = e.target;
        if (files?.length) {
            onFilesAdded(files, Array.from(files).some(isFolderFile));
        }
        e.target.value = ''; // allow re-dropping the same file after removal
    };

    return (
        <div
            id="usd-import-dropzone"
            className={`usd-import-dropzone ${isDragging ? 'dragging' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
        >
            {isDragging ? 'Release to add files for parsing' : 'Drop files or folders here or click to browse'}
            <span
                id="usd-import-dropzone-folder-link"
                className="usd-import-dropzone-folder-link"
                onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
            >
                or select a folder
            </span>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="usd-import-dropzone-input"
                onChange={handleFileInput}
            />
            <input
                ref={folderInputRef}
                type="file"
                webkitdirectory=""
                directory=""
                multiple
                className="usd-import-dropzone-input"
                onChange={handleFileInput}
            />
        </div>
    );
}
